import React, { useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { QRCodeSVG } from 'qrcode.react';

import type { DisplayConceptRef } from '@/lib/api';
import { buildInviteUrl, copyInviteUrl, type PendingInvite } from '@/lib/invites';

import { RemoveTopicButton } from './RemoveButton';
import { TopicSelector } from './TopicSelector';

export interface InviteFormValues {
  inviterTopics: DisplayConceptRef[];
  inviteeTopics: DisplayConceptRef[];
  nickname?: string;
}

export interface InviteFormProps {
  introMessage?: React.ReactNode;
  disabled?: boolean;
  onSubmit: (values: InviteFormValues) => void;
  onCancel?: () => void;
}

/** Collects invitation personalization without owning requests or mutation state. */
export const InviteForm: React.FC<InviteFormProps> = ({
  introMessage,
  disabled = false,
  onSubmit,
  onCancel,
}) => {
  const [inviterTopics, setInviterTopics] = useState<DisplayConceptRef[]>([]);
  const [inviteeTopics, setInviteeTopics] = useState<DisplayConceptRef[]>([]);
  const [nickname, setNickname] = useState('');

  const handleTopicSelect = (topic: DisplayConceptRef | null, inviterFollows: boolean) => {
    if (!topic) return;
    if (inviterFollows) {
      setInviterTopics((topics) => [...topics, topic]);
    } else {
      setInviteeTopics((topics) => [...topics, topic]);
    }
  };

  const removeTopic = (index: number, inviterFollows: boolean) => {
    if (inviterFollows) {
      setInviterTopics((topics) => topics.filter((_, topicIndex) => topicIndex !== index));
    } else {
      setInviteeTopics((topics) => topics.filter((_, topicIndex) => topicIndex !== index));
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        const normalizedNickname = nickname.trim();
        onSubmit({
          inviterTopics,
          inviteeTopics,
          ...(normalizedNickname ? { nickname: normalizedNickname } : {}),
        });
      }}
    >
      {introMessage === undefined ? (
        <p>Bring on a friend and share your expertise together!</p>
      ) : (
        introMessage
      )}
      <Box sx={{ mb: 3 }}>
        <TextField
          id="nickname"
          label="A nickname to personalize this invite"
          value={nickname}
          onChange={(event) => setNickname(event.target.value)}
          disabled={disabled}
          fullWidth
        />
      </Box>
      <div role="group" aria-labelledby="following-topics-label" className="form-group">
        <label id="following-topics-label">
          (Optional) Topics you want to follow your friend on:
        </label>
        <TopicSelector
          instanceId="invite-following-topic-selector"
          onChangeFn={(topic) => handleTopicSelect(topic, true)}
          aria-label="Select topics you want to follow your friend on"
        />
        <List dense aria-label="Selected topics to follow your friend on" sx={{ mt: 1 }}>
          {inviterTopics.map((topic, index) => (
            <ListItem
              key={`${topic.cref}-${index}`}
              secondaryAction={
                <RemoveTopicButton
                  topic={topic.display}
                  onRemove={() => removeTopic(index, true)}
                />
              }
              sx={{ pl: 0 }}
            >
              <ListItemText primary={topic.display} />
            </ListItem>
          ))}
        </List>
      </div>
      <div role="group" aria-labelledby="followed-topics-label" className="form-group">
        <label id="followed-topics-label">
          (Optional) Topics your friend might want to follow you on:
        </label>
        <TopicSelector
          instanceId="invite-followed-topic-selector"
          onChangeFn={(topic) => handleTopicSelect(topic, false)}
          aria-label="Select topics your friend might want to follow you on"
        />
        <List dense aria-label="Selected topics for your friend to follow you on" sx={{ mt: 1 }}>
          {inviteeTopics.map((topic, index) => (
            <ListItem
              key={`${topic.cref}-${index}`}
              secondaryAction={
                <RemoveTopicButton
                  topic={topic.display}
                  onRemove={() => removeTopic(index, false)}
                />
              }
              sx={{ pl: 0 }}
            >
              <ListItemText primary={topic.display} />
            </ListItem>
          ))}
        </List>
      </div>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        {onCancel && (
          <Button type="button" onClick={onCancel} variant="outlined" disabled={disabled}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={disabled} variant="contained">
          {disabled ? 'Creating...' : 'Create Invite'}
        </Button>
      </Box>
    </form>
  );
};

interface InviteSuccessProps {
  invite: PendingInvite;
  showQrCode?: boolean;
  onDone?: () => void;
  onCopyError: () => void;
}

/** Displays the committed invitation while the manager retains its ownership. */
export const InviteSuccess: React.FC<InviteSuccessProps> = ({
  invite,
  showQrCode = false,
  onDone,
  onCopyError,
}) => {
  const inviteUrl = buildInviteUrl(invite.code);
  return (
    <Box component="section" aria-live="polite">
      <Stack spacing={2} alignItems="center">
        <Typography variant="h5" component="h2">
          Invite created
        </Typography>
        <Typography component="p" sx={{ overflowWrap: 'anywhere', textAlign: 'center' }}>
          <Box component="span" sx={{ fontWeight: 500 }}>
            Invite Code:{' '}
          </Box>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              ml: 0.75,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              border: '1px solid var(--mui-palette-divider)',
              backgroundColor: 'var(--mui-palette-background-paper)',
              fontFamily: 'monospace',
              fontWeight: 600,
            }}
          >
            {invite.code}
          </Box>
        </Typography>
        {showQrCode && (
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: 280, sm: 320 },
              mx: 'auto',
              '& svg': { display: 'block', width: '100%', height: 'auto' },
            }}
          >
            <QRCodeSVG value={inviteUrl} includeMargin />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            flexWrap: 'wrap',
            overflowWrap: 'anywhere',
          }}
        >
          <code>
            <Link href={inviteUrl} target="_blank" rel="noopener noreferrer">
              {inviteUrl}
            </Link>
          </code>
          <IconButton
            aria-label="Copy invite link"
            onClick={async () => {
              try {
                await copyInviteUrl(invite.code);
              } catch {
                onCopyError();
              }
            }}
            size="small"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
        {invite.thread_context && (
          <Typography component="p" sx={{ textAlign: 'center' }}>
            After your friend signs up, they&apos;ll be directed to your post.
          </Typography>
        )}
        {onDone && (
          <Button onClick={onDone} variant="contained">
            Done
          </Button>
        )}
      </Stack>
    </Box>
  );
};
